import React, { useEffect, useState } from 'react';
import FileDropZone from '../FileDropZone'
import { supabase } from '../../lib/supabase';
import './DDDocumentCollection.css';

interface RequiredDocument {
  type: string;
  name: string;
  required: boolean;
  description: string;
}

interface UploadedDocument {
  id: string;
  type: string;
  filePath: string;
  status: 'pending' | 'validating' | 'pass' | 'fail' | 'manual_review';
  confidence?: number;
  extractedName?: string;
  extractedAddress?: string;
  uploadedAt: string;
}

interface DDDocumentCollectionProps {
  dealId: string;
  sellerType: 'individual' | 'company' | 'dealer' | 'auction' | 'tender';
  sellerInfo: {
    name: string;
    email: string;
    phone: string;
  };
  onCollectionComplete?: () => void;
}

const DOCUMENT_REQUIREMENTS: Record<string, RequiredDocument[]> = {
  individual: [
    { type: 'DL_FRONT', name: 'Driver Licence (Front)', required: true, description: 'Current, not expired' },
    { type: 'DL_BACK', name: 'Driver Licence (Back)', required: true, description: 'Current, not expired' },
    { type: 'SELFIE_ID', name: 'Selfie ID', required: true, description: 'Hold licence or device, face visible' },
    { type: 'REGO', name: 'Registration Certificate', required: true, description: 'Current rego matching seller name' },
    { type: 'BANK', name: 'Bank Account Details', required: true, description: 'Account name must match seller name' },
    { type: 'PAYOUT', name: 'Finance Payout Letter', required: false, description: 'Only if vehicle under finance' },
    { type: 'PPSR', name: 'PPSR Search Certificate', required: true, description: 'Auto-generated - compulsory' },
    { type: 'ABR', name: 'ABR Lookup', required: true, description: 'Auto-generated - compulsory' }
  ],
  company: [
    { type: 'COMPANY_CERT', name: 'Company Vehicle Registration Certificate', required: true, description: 'Current registration' },
    { type: 'ASIC', name: 'Full Company Extract with Director details', required: true, description: 'Recent, director names must match IDs' },
    { type: 'DIRECTOR_ID_1', name: 'Director 1 ID (Front & Back)', required: true, description: 'Current, not expired' },
    { type: 'DIRECTOR_ID_2', name: 'Director 2 ID (Front & Back)', required: false, description: 'If 2+ directors' },
    { type: 'SELFIE_ID', name: 'Selfie ID (Director)', required: true, description: 'Face must match director ID' },
    { type: 'TAX_INVOICE', name: 'Tax Invoice', required: true, description: 'ABN must match company' },
    { type: 'COMPANY_BANK', name: 'Company Bank Account Details', required: true, description: 'Account name must match' },
    { type: 'PPSR', name: 'PPSR Search Certificate', required: true, description: 'Auto-generated - compulsory' },
    { type: 'ABR', name: 'ABR Lookup', required: true, description: 'Auto-generated - compulsory' }
  ],
  dealer: [
    { type: 'TAX_INVOICE', name: 'Tax Invoice', required: true, description: 'Valid tax invoice from dealer' },
    { type: 'PPSR', name: 'PPSR Search Certificate', required: true, description: 'Auto-generated - compulsory' },
    { type: 'DEALER_BANK', name: 'Dealer Account Bank Details', required: true, description: 'Account name must match' },
    { type: 'AUCTION_TRUST', name: 'Auction Trust Account', required: false, description: 'If auction deal' },
    { type: 'LMCT_LICENCE', name: 'LMCT/MDL Licence', required: false, description: 'If applicable' }
  ],
  // Auction and tender were added to the seller types on 24 August. Without
  // their own entries here the lookup below falls through to an empty array,
  // which reads on screen as a deal with no due diligence outstanding.
  auction: [
    { type: 'TAX_INVOICE', name: 'Tax Invoice', required: true, description: 'Valid tax invoice from the auction house' },
    { type: 'PPSR', name: 'PPSR Search Certificate', required: true, description: 'Auto-generated - compulsory' },
    { type: 'DEALER_BANK', name: 'Auction House Bank Details', required: true, description: 'Account name must match the invoice' },
    { type: 'AUCTION_TRUST', name: 'Auction Trust Account', required: true, description: 'Confirmation the funds go to the trust account' },
    { type: 'LMCT_LICENCE', name: 'LMCT/MDL Licence', required: false, description: 'If applicable' }
  ],
  tender: [
    { type: 'TAX_INVOICE', name: 'Tax Invoice', required: true, description: 'Valid tax invoice from the disposing entity' },
    { type: 'PPSR', name: 'PPSR Search Certificate', required: true, description: 'Auto-generated - compulsory' },
    { type: 'DEALER_BANK', name: 'Seller Bank Details', required: true, description: 'Account name must match the invoice' },
    { type: 'LMCT_LICENCE', name: 'LMCT/MDL Licence', required: false, description: 'If applicable' }
  ]
};

/*
 * The seller portal and this panel name the same documents differently. The
 * portal slugs the label it showed the seller, so "Driver Licence (front)"
 * is stored as driver_licence; this panel calls that DL_FRONT. Without a
 * translation between them the panel reads a complete pack as nothing at all.
 *
 * sellerPortal.js already notes that Winnie's rule lives in one place because
 * three files were about to each keep their own copy and one of them was
 * already wrong. This panel was the fourth. The translation lives here, beside
 * the requirements it translates, so the two are read together.
 *
 * DIRECTOR_ID_2 has no portal equivalent on purpose: the portal collects all
 * directors in a single upload, so a company pack sent by the seller satisfies
 * the first slot and leaves the second genuinely outstanding.
 */
const PORTAL_TYPES: Record<string, string[]> = {
  DL_FRONT: ['driver_licence'],
  DL_BACK: ['driver_licence_back'],
  SELFIE_ID: ['selfie_id'],
  REGO: ['registration_certificate'],
  BANK: ['bank_account_details'],
  PAYOUT: ['finance_payout_letter'],
  PPSR: ['ppsr_search_certificate', 'ppsr_certificate'],
  ABR: ['abr_lookup'],
  COMPANY_CERT: ['company_registration_certificate'],
  ASIC: ['asic_extract'],
  DIRECTOR_ID_1: ['director_id'],
  TAX_INVOICE: ['tax_invoice', 'tax_invoice_or_abn_invoice'],
  COMPANY_BANK: ['company_bank_account_details'],
  DEALER_BANK: ['dealer_bank_account_details'],
  AUCTION_TRUST: ['auction_trust_account'],
  LMCT_LICENCE: ['lmct_mdl_licence'],
}

export const DDDocumentCollection: React.FC<DDDocumentCollectionProps> = ({
  dealId,
  sellerType,
  sellerInfo,
  onCollectionComplete
}) => {
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)

  /*
   * Load what is already on the deal.
   *
   * This panel used to open empty every time and count only what was uploaded
   * inside that one session. A seller who sent all five documents through their
   * portal - the normal path, and the one the portal thanks them for - left
   * staff looking at "Progress: 0/5" and "Still needed:" listing the whole pack,
   * with Submit All Documents disabled. The two halves of the system described
   * the same deal in contradictory terms, and the half a person acts on was the
   * wrong one.
   */
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const { data, error: loadError } = await supabase
        .from('dd_documents')
        .select(
          'id, document_type, document_file_path, validation_status, validation_confidence, extracted_name, extracted_address, uploaded_at',
        )
        .eq('deal_id', dealId)
        .order('uploaded_at', { ascending: true })
      if (cancelled) return
      if (loadError) {
        setError(
          'Could not read the documents already on this deal. Refresh before uploading, so the seller is not asked again for something we already hold.',
        )
        return
      }
      const requirements = DOCUMENT_REQUIREMENTS[sellerType] || DOCUMENT_REQUIREMENTS.individual
      const byPortalType = new Map<string, string>()
      requirements.forEach((req) => {
        const aliases = PORTAL_TYPES[req.type] || []
        aliases.forEach((alias) => byPortalType.set(alias, req.type))
      })
      const rows: UploadedDocument[] = []
      ;(data || []).forEach((row: Record<string, unknown>) => {
        const raw = String(row.document_type || '')
        const type = byPortalType.get(raw) || raw
        const mapped: UploadedDocument = {
          id: String(row.id),
          type,
          filePath: String(row.document_file_path || ''),
          status: (row.validation_status || 'pending') as UploadedDocument['status'],
          confidence: Number(row.validation_confidence ?? 0),
          extractedName: String(row.extracted_name || ''),
          extractedAddress: String(row.extracted_address || ''),
          uploadedAt: String(row.uploaded_at || ''),
        }
        /*
         * A replaced document is written as a new row under the same type, so
         * the newest row is the one on file. Rows arrive oldest first, so a
         * later one overwrites the slot rather than filling a second.
         */
        const slot = rows.findIndex((r) => r.type === type)
        if (slot >= 0) rows[slot] = mapped
        else rows.push(mapped)
      })
      setDocuments(rows)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [dealId, sellerType]);

  // PPSR and ABR are marked [AUTO] and have no upload control, but they used to
  // be counted as required — so for individual and company sellers the required
  // total could never be met and "Submit All Documents" stayed disabled forever.
  // For dealers the opposite happened: optional uploads counted towards the
  // required total, so you could submit with required documents missing.
  const AUTO_TYPES = ['PPSR', 'ABR'];
  const isAutoType = (t: string) => AUTO_TYPES.includes(t);

  const requirements = DOCUMENT_REQUIREMENTS[sellerType] || [];
  const requiredUploadable = requirements.filter((d) => d.required && !isAutoType(d.type));
  const requiredCount = requiredUploadable.length;
  const uploadedRequired = requiredUploadable.filter((d) => documents.some((doc) => doc.type === d.type));
  const uploadedCount = uploadedRequired.length;
  const missingRequired = requiredUploadable.filter((d) => !documents.some((doc) => doc.type === d.type));
  const progressPercent = requiredCount > 0 ? (uploadedCount / requiredCount) * 100 : 0;

  // An unrecognised seller type used to yield an empty requirement list, which
  // made requiredCount 0 and enabled Submit with zero documents.
  const unknownSellerType = requirements.length === 0;

  const handleFileUpload = async (file: File | undefined, docType: string) => {
    if (!file) return;

    const MAX_BYTES = 15 * 1024 * 1024;
    const ALLOWED = ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf'];
    if (file.size > MAX_BYTES) {
      setError(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB. Please upload a file under 15 MB.`);
      return;
    }
    if (file.type && !ALLOWED.includes(file.type)) {
      setError('Please upload a photo (JPG, PNG, HEIC, WEBP) or a PDF.');
      return;
    }

    setUploadingDocType(docType);
    setError(null);

    try {
      // Upload to Supabase Storage
      const fileName = `dd-docs/${dealId}/${docType}-${Date.now()}.${file.name.split('.').pop()}`;
      const { error: uploadError } = await supabase.storage
        .from('dd-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Create document record in DB
      const { data, error: dbError } = await supabase
        .from('dd_documents')
        .insert([{
          deal_id: dealId,
          document_type: docType,
          document_file_path: fileName,
          uploaded_by_role: 'seller',
          uploaded_at: new Date().toISOString(),
          // There is no OCR/extraction step in the platform, so no document can
          // be auto-validated. Marking them 'pending' meant they never matched
          // the DD Review query (manual_review / hard_block + manual_review_required)
          // and the queue was permanently empty — which in turn meant no deal
          // could ever legitimately reach Committed.
          validation_status: 'manual_review',
          manual_review_required: true,
          manual_review_reason: 'Awaiting human review — automated document extraction is not enabled.'
        }])
        .select()
        .single();

      if (dbError) throw dbError;

      // Add to local state
      setDocuments([...documents, {
        id: data.id,
        type: docType,
        filePath: fileName,
        status: 'manual_review',
        uploadedAt: data.uploaded_at
      }]);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMsg);
    } finally {
      setUploadingDocType(null);
      // Without this, re-picking the *same* file after a failed upload fires no
      // change event, so the retry the user is most likely to attempt does nothing.
    }
  };

  /** Upload the wrong photo and there used to be no way back — no delete, no replace. */
  const handleRemove = async (docId: string) => {
    setError(null);
    const previous = documents;
    setDocuments(documents.filter((d) => d.id !== docId));
    try {
      const { error: delError } = await supabase.from('dd_documents').delete().eq('id', docId);
      if (delError) throw delError;
    } catch (err) {
      setDocuments(previous);
      setError(err instanceof Error ? err.message : 'Could not remove that document');
    }
  };

  /*
   * Winnie, 1 Sep: "Should only be able to submit for review once - if already
   * in the DD Review queue unable to re-submit."
   *
   * Re-submitting pushed the same pack back into the queue and reset the
   * reviewer's place in it. The deal's own dd_status is the authority, so it
   * is read here rather than inferred from local state.
   */
  useEffect(() => {
    let cancelled = false
    supabase
      .from('deals')
      .select('dd_status')
      .eq('id', dealId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        const s = String((data && data.dd_status) || '').toLowerCase()
        setAlreadySubmitted(s === 'submitted' || s === 'approved')
      })
    return () => {
      cancelled = true
    }
  }, [dealId])

  const handleSubmit = async () => {
    if (alreadySubmitted) {
      setError('This pack is already with DD Review. It cannot be submitted twice.')
      return
    }

    if (unknownSellerType) {
      setError('No document set is defined for this seller type. Set the seller type on the deal first.');
      return;
    }
    if (uploadedCount < requiredCount) {
      setError(
        `Still needed: ${missingRequired.map((d) => d.name).join(', ')}`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // Update deal DD status to submitted
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          dd_status: 'submitted',
          dd_submitted_at: new Date().toISOString()
        })
        .eq('id', dealId);

      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => {
        if (onCollectionComplete) onCollectionComplete();
      }, 2000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Submission failed';
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocumentStatus = (docType: string): UploadedDocument | undefined => {
    return documents.find(d => d.type === docType);
  };

  if (success) {
    return (
      <div className="dd-collection-container">
        <div className="dd-success">
          <div className="success-icon">✓</div>
          <h3>Documents Submitted</h3>
          <p>The due diligence documents have been received.</p>
          <p>They will now appear in the DD Review queue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dd-collection-container">
      <div className="dd-collection-card">
        <h2>Due Diligence Document Collection</h2>

        <div className="dd-intro">
          <h3>Seller: {sellerInfo.name}</h3>
          <p className="seller-type">Type: {sellerType.charAt(0).toUpperCase() + sellerType.slice(1)}</p>
        </div>

        <div className="dd-progress-section">
          <div className="progress-header">
            <span className="progress-label">Progress: {uploadedCount}/{requiredCount} documents</span>
            <span className="progress-percent">{Math.round(progressPercent)}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className="dd-documents-list">
          {requirements.map(req => {
            const uploaded = getDocumentStatus(req.type);
            const isAuto = req.type === 'PPSR' || req.type === 'ABR';

            return (
              <div key={req.type} className={`dd-document-row ${uploaded ? 'uploaded' : ''}`}>
                <div className="document-info">
                  <label className="document-name">
                    {req.required ? '☐ ' : '◯ '}{req.name}
                  </label>
                  <p className="document-desc">{req.description}</p>
                </div>

                <div className="document-status">
                  {isAuto ? (
                    <>
                      <span className="status-badge auto">[AUTO]</span>
                      <span className="status-text">
                        {uploaded ? 'On file' : 'Handled by the platform — not required from the seller'}
                      </span>
                    </>
                  ) : uploaded ? (
                    <>
                      <span className="status-badge success">✓ UPLOADED</span>
                      {uploaded.status === 'pass' ? (
                        <span className="status-text">Validated</span>
                      ) : (
                        <span className="status-text">Queued for DD Review</span>
                      )}
                      <button
                        type="button"
                        className="doc-remove"
                        onClick={() => handleRemove(uploaded.id)}
                        title="Remove this document and upload a different one"
                      >
                        Replace
                      </button>
                    </>
                  ) : (
                    <FileDropZone
                      accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
                      label={uploadingDocType === req.type ? 'Uploading…' : 'Upload'}
                      busy={uploadingDocType === req.type}
                      busyLabel="Uploading…"
                      disabled={uploadingDocType !== null}
                      hint="A photo or a PDF, up to 15 MB."
                      onFiles={(files) => handleFileUpload(files[0], req.type)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="dd-actions">
          <button
            className="btn-submit-dd"
            onClick={handleSubmit}
            disabled={unknownSellerType || uploadedCount < requiredCount || isSubmitting || alreadySubmitted}
          >
            {isSubmitting ? 'Submitting...' : 'Submit All Documents'}
          </button>
          <p className="action-note">
            {unknownSellerType
              ? 'No document set is defined for this seller type — set the seller type on the deal first.'
              : missingRequired.length > 0
              ? `Still needed: ${missingRequired.map((d) => d.name).join(', ')}`
              : 'All required documents are on file. Submitting sends them to DD Review.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DDDocumentCollection;
